import { FC, useEffect, useMemo, useState } from "react";
import { Modal, Button, Alert, Form, Table } from "react-bootstrap";
import Loader from "src/components/common/loader/loader";
import { Invoice, InvoiceLineItem } from "src/shared/models/invoice.model";
import { InvoiceService } from "@services/finance/invoices/invoiceService";

interface Props {
  show: boolean;
  onClose: () => void;
  organizationId: number;
  organizationName: string;
}

const invoiceService = new InvoiceService();

const currentMonth = () => new Date().toISOString().slice(0, 7);

// month is "YYYY-MM"; end is exclusive per the API (June = start 06-01, end 07-01)
const monthRange = (month: string) => {
  const [y, m] = month.split("-").map(Number);
  const start = `${month}-01`;
  const end = m === 12 ? `${y + 1}-01-01` : `${y}-${String(m + 1).padStart(2, "0")}-01`;
  return { start, end };
};

// Money arrives as decimal strings — format without parseFloat to avoid float rounding.
const formatMoney = (value: string | null) => {
  if (value == null) return "—";
  const [whole, cents] = value.split(".");
  const negative = whole.startsWith("-");
  const digits = negative ? whole.slice(1) : whole;
  const grouped = digits.replace(/\B(?=(\d{3})+(?!\d))/g, ",");
  return `${negative ? "-" : ""}$${grouped}${cents != null ? `.${cents}` : ""}`;
};

const itemStatus = (item: InvoiceLineItem) => {
  if (item.is_reversion) return { label: "Reversion", dot: "text-danger" };
  if (item.is_reconvert) return { label: "Reconvert", dot: "text-secondary" };
  if (item.is_preconvert) return { label: "Preconvert", dot: "text-secondary" };
  if (item.is_control) return { label: "Control", dot: "text-secondary" };
  if (item.billable) return { label: "Billable", dot: "text-success" };
  return { label: "Not billable", dot: "text-secondary" };
};

const StatusCell: FC<{ item: InvoiceLineItem }> = ({ item }) => {
  const { label, dot } = itemStatus(item);
  return (
    <span className="text-nowrap">
      <i
        className={`bi bi-circle-fill me-2 ${dot}`}
        style={{ fontSize: 7, verticalAlign: "middle" }}
      />
      {label}
    </span>
  );
};

const InvoiceInfoModal: FC<{ show: boolean; onClose: () => void }> = ({ show, onClose }) => (
  <Modal
    show={show}
    onHide={onClose}
    size="lg"
    centered
    scrollable
    className="tier-2-modal"
    backdropClassName="tier-2-backdrop"
    contentClassName="h-auto"
  >
    <Modal.Header closeButton>
      <Modal.Title as="h5">
        <i className="bi bi-info-circle me-2" />
        How these numbers are calculated
      </Modal.Title>
    </Modal.Header>
    <Modal.Body>
      <p>
        Every row in this report is one conversion event. Each total is a separate count of rows.
        Nothing is subtracted, netted, or hidden. Rows that shouldn't be billed are flagged and
        left out of Billable, but they stay visible in the report.
      </p>
      <dl className="mb-0">
        <dt>Conversions</dt>
        <dd>
          Rows where a consumer newly met a conversion rule (not a reversion, not a preconvert,
          not a reconvert). This is real conversion activity from the campaign.
        </dd>
        <dt>Preconverts</dt>
        <dd>
          Rows where the consumer had already converted when they first entered the population.
          We keep them for the audit trail, but the campaign didn't drive them. Never billable,
          and not counted in Conversions.
        </dd>
        <dt>Reversions</dt>
        <dd>
          Rows where a previous conversion flipped back, like a student un-enrolling in a later
          file. A reversion row is never billable, and it doesn't automatically cancel the
          original conversion in this report. If the original was billed in an earlier period,
          you decide whether to issue a credit. Both rows stay visible so you can net them out
          however billing policy says.
        </dd>
        <dt>Reconverts</dt>
        <dd>
          Rows where the consumer converted again on a pricesheet item and dimension they had
          already converted on at an earlier date — for example convert, revert, convert again.
          A consumer is billed at most once per pricesheet item and dimension, looking back
          across all time (not just this billing period), so only the first conversion can be
          billable. Reconverts are never billable and not counted in Conversions.
        </dd>
        <dt>Control</dt>
        <dd>
          Rows for consumers in the holdout group of their population segment. They converted,
          but we deliberately didn't serve them ads, so the campaign can't take credit for them.
          Never billable.
        </dd>
        <dt>Billable</dt>
        <dd>
          The count of rows that pass all four checks: not a preconvert, not a reversion, not a
          reconvert, and not in the control group.
        </dd>
        <dt>Billable Total</dt>
        <dd>
          The sum of each billable row's price, where a row's price is its base price plus its
          step price. Treat it as a reconciliation aid, not a final invoice amount. It doesn't
          net out reversions of previously billed conversions and it doesn't dedupe consumers who
          converted on multiple steps. Apply those policies downstream.
        </dd>
      </dl>
      <p className="text-muted small mb-0 mt-3">
        A row can carry more than one flag (for example a control member's reversion). It's
        counted in each matching category, but Billable is never double-counted because any flag
        excludes the row.
      </p>
    </Modal.Body>
    <Modal.Footer>
      <Button variant="light" onClick={onClose}>
        Close
      </Button>
    </Modal.Footer>
  </Modal>
);

type SortKey =
  | "billing_date"
  | "consumer"
  | "engagement"
  | "contract"
  | "step"
  | "dimension"
  | "pricesheet"
  | "base_price"
  | "price"
  | "status";

const sortValue = (item: InvoiceLineItem, key: SortKey): string | number => {
  switch (key) {
    case "billing_date":
      return item.billing_date;
    case "consumer":
      return item.consumer_id;
    case "engagement":
      return item.engagement_name;
    case "contract":
      return item.contract_name;
    case "step":
      return item.journey_step_sequence;
    case "dimension":
      return item.dimension_name ?? "";
    case "pricesheet":
      return item.pricesheet_item_name ?? "";
    case "base_price":
      return Number(item.base_price ?? 0);
    case "price":
      return Number(item.step_price ?? 0);
    case "status":
      return itemStatus(item).label;
  }
};

interface SortState {
  key: SortKey;
  dir: 1 | -1;
}

const SortHeader: FC<{
  label: string;
  sortKey: SortKey;
  sort: SortState | null;
  onSort: (key: SortKey) => void;
  className?: string;
}> = ({ label, sortKey, sort, onSort, className = "" }) => {
  const active = sort?.key === sortKey;
  return (
    <th
      role="button"
      className={`user-select-none text-nowrap bg-white ${className}`}
      // position: sticky is ignored on <thead> in Chrome, so each th sticks itself;
      // the box-shadow stands in for the header border, which doesn't stick either
      style={{ position: "sticky", top: 0, zIndex: 1, boxShadow: "inset 0 -1px 0 #dee2e6" }}
      onClick={() => onSort(sortKey)}
    >
      {label}
      {active && (
        <i className={`bi ms-1 ${sort.dir === 1 ? "bi-caret-up-fill" : "bi-caret-down-fill"}`} />
      )}
    </th>
  );
};

const OrganizationInvoiceModal: FC<Props> = ({ show, onClose, organizationId, organizationName }) => {
  const [month, setMonth] = useState(currentMonth);
  const [invoice, setInvoice] = useState<Invoice | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [isExporting, setIsExporting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [sort, setSort] = useState<SortState | null>(null);
  const [showInfo, setShowInfo] = useState(false);

  const params = useMemo(() => (month ? monthRange(month) : undefined), [month]);

  useEffect(() => {
    if (!show) return;
    let cancelled = false;
    setIsLoading(true);
    setError(null);
    invoiceService
      .getOrganizationInvoice(organizationId, params)
      .then((result) => {
        if (!cancelled) setInvoice(result);
      })
      .catch((err: Error) => {
        if (!cancelled) setError(err.message || "Failed to load invoice");
      })
      .finally(() => {
        if (!cancelled) setIsLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [show, organizationId, params]);

  const handleExport = async () => {
    setIsExporting(true);
    setError(null);
    try {
      await invoiceService.exportOrganizationInvoice(organizationId, params);
    } catch (err) {
      setError((err as Error).message || "Failed to export invoice");
    } finally {
      setIsExporting(false);
    }
  };

  const totals = invoice?.totals;
  const items = useMemo(() => {
    const lineItems = invoice?.line_items ?? [];
    if (!sort) return lineItems;
    return [...lineItems].sort((a, b) => {
      const va = sortValue(a, sort.key);
      const vb = sortValue(b, sort.key);
      const cmp =
        typeof va === "number" && typeof vb === "number"
          ? va - vb
          : String(va).localeCompare(String(vb));
      return cmp * sort.dir;
    });
  }, [invoice, sort]);

  const toggleSort = (key: SortKey) =>
    setSort((prev) => (prev?.key === key ? { key, dir: prev.dir === 1 ? -1 : 1 } : { key, dir: 1 }));

  // Names can arrive as "" — hide columns that are empty across the whole invoice.
  const hasContract = items.some((i) => i.contract_name);
  const hasDimension = items.some((i) => i.dimension_name);
  const showSequence = new Set(items.map((i) => i.journey_step_sequence)).size > 1;

  const stats: Array<{ label: string; value: string | number }> = totals
    ? [
        { label: "Billable", value: totals.billable },
        { label: "Conversions", value: totals.conversions },
        { label: "Reversions", value: totals.reversions },
        { label: "Reconverts", value: totals.reconverts },
        { label: "Preconverts", value: totals.preconverts },
        { label: "Control", value: totals.control },
        { label: "Billable Total", value: formatMoney(totals.billable_total) },
      ]
    : [];

  return (
    <Modal show={show} onHide={onClose} size="xl" centered dialogClassName="invoice-modal-wide">
      <Modal.Header closeButton>
        <Modal.Title as="h5">
          <i className="bi bi-receipt me-2" />
          Invoice for {invoice?.organization_name || organizationName}
        </Modal.Title>
      </Modal.Header>
      <Modal.Body className="d-flex flex-column" style={{ height: "72vh" }}>
        <div className="d-flex align-items-center gap-2 mb-3 flex-wrap">
          <Form.Label className="fw-semibold mb-0">Billing Period</Form.Label>
          <Form.Control
            type="month"
            value={month}
            onChange={(e) => setMonth(e.target.value)}
            style={{ width: 200 }}
          />
          <span className="text-muted small">Clear the month to view all time.</span>
          <Button
            variant="link"
            className="ms-auto p-0 text-muted"
            title="How these numbers are calculated"
            onClick={() => setShowInfo(true)}
          >
            <i className="bi bi-info-circle fs-5" />
          </Button>
        </div>

        {totals && (
          <div className="d-flex gap-2 mb-3 flex-wrap">
            {stats.map((stat) => (
              <div key={stat.label} className="border rounded px-3 py-2 flex-fill text-center">
                <div
                  className="text-muted text-uppercase"
                  style={{ fontSize: "0.68rem", letterSpacing: "0.04em" }}
                >
                  {stat.label}
                </div>
                <div className={`fs-5 fw-semibold${stat.value === 0 ? " text-muted" : ""}`}>
                  {stat.value}
                </div>
              </div>
            ))}
          </div>
        )}

        {error && <Alert variant="danger" className="mb-3">{error}</Alert>}

        {invoice?.warnings.map((warning) => (
          <Alert key={warning} variant="warning" className="mb-3 py-2">
            <i className="bi bi-exclamation-triangle me-2" />
            {warning}
          </Alert>
        ))}

        {isLoading ? (
          <Loader />
        ) : items.length === 0 ? (
          <div className="text-center text-muted py-5">
            <i className="bi bi-inbox fs-2 d-block mb-2" />
            No line items for this period.
          </div>
        ) : (
          <div className="flex-grow-1 overflow-auto border rounded" style={{ minHeight: 0 }}>
            <Table hover size="sm" className="align-middle mb-0">
              <thead>
                <tr>
                  <SortHeader label="Billing Date" sortKey="billing_date" sort={sort} onSort={toggleSort} />
                  <SortHeader label="Consumer" sortKey="consumer" sort={sort} onSort={toggleSort} />
                  <SortHeader label="Engagement" sortKey="engagement" sort={sort} onSort={toggleSort} />
                  {hasContract && (
                    <SortHeader label="Contract" sortKey="contract" sort={sort} onSort={toggleSort} />
                  )}
                  <SortHeader label="Step" sortKey="step" sort={sort} onSort={toggleSort} />
                  {hasDimension && (
                    <SortHeader label="Dimension" sortKey="dimension" sort={sort} onSort={toggleSort} />
                  )}
                  <SortHeader label="Pricesheet Item" sortKey="pricesheet" sort={sort} onSort={toggleSort} />
                  <SortHeader label="Base Price" sortKey="base_price" sort={sort} onSort={toggleSort} className="text-end" />
                  <SortHeader label="Step Price" sortKey="price" sort={sort} onSort={toggleSort} className="text-end pe-4" />
                  <SortHeader label="Status" sortKey="status" sort={sort} onSort={toggleSort} className="ps-3" />
                </tr>
              </thead>
              <tbody>
                {items.map((item) => (
                  <tr key={item.trace_id} className={item.billable ? "" : "text-muted"}>
                    <td className="text-nowrap">{item.billing_date}</td>
                    <td>{item.consumer_id}</td>
                    <td>{item.engagement_name || "—"}</td>
                    {hasContract && <td>{item.contract_name || "—"}</td>}
                    <td>
                      {item.journey_step_name || "—"}
                      {showSequence && (
                        <span className="text-muted small ms-1">#{item.journey_step_sequence}</span>
                      )}
                    </td>
                    {hasDimension && <td>{item.dimension_name || "—"}</td>}
                    <td>{item.pricesheet_item_name || "—"}</td>
                    <td className="text-end text-nowrap">{formatMoney(item.base_price)}</td>
                    <td className="text-end text-nowrap pe-4">{formatMoney(item.step_price)}</td>
                    <td className="ps-3"><StatusCell item={item} /></td>
                  </tr>
                ))}
              </tbody>
            </Table>
          </div>
        )}
      </Modal.Body>
      <Modal.Footer>
        {totals && (
          <div className="me-auto text-muted small">
            {totals.line_items} line item{totals.line_items === 1 ? "" : "s"}
          </div>
        )}
        <Button variant="light" onClick={onClose}>
          Close
        </Button>
        <Button variant="primary" onClick={handleExport} disabled={isExporting || isLoading}>
          {isExporting ? (
            <>
              <span className="spinner-border spinner-border-sm me-2" /> Exporting...
            </>
          ) : (
            <>
              <i className="bi bi-download me-2" />
              Export CSV
            </>
          )}
        </Button>
      </Modal.Footer>
      <InvoiceInfoModal show={showInfo} onClose={() => setShowInfo(false)} />
    </Modal>
  );
};

export default OrganizationInvoiceModal;
